/* ──────────────────────────────────────────────────────────────
 *  useShoreScaling — Shore A hardness scaling for output values
 *
 *  Ports the Python formula: scale = 1.0 + 0.035 * (newShA - 55)
 *  Maintains baselines per sample, debounces slider input, and
 *  updates gen-row values + triggers table/plot refresh.
 * ────────────────────────────────────────────────────────────── */

import { useCallback, useRef } from 'react';
import { useSampleStore } from '@/stores/useSampleStore';
import { useBushingStore } from '@/stores/useBushingStore';
import type { GenRow } from '@/types/sample';
import type { BushingType } from '@/types/bushing';

const BASELINE_SHORE = 55;
const DEBOUNCE_MS = 50;

/** Columns to scale for each bushing type family. */
function getScalableColumns(bushingType: BushingType): string[] {
  switch (bushingType) {
    case 'D1':
    case 'BYD EV Voided':
      return ['Cx', 'Cy', 'Cz', 'Torsion'];
    case 'BYD EV Solid':
      return ['Cx', 'Cz'];
    default:
      // A1, B1, C1
      return ['Cx', 'Cz', 'Torsion'];
  }
}

/** Compute the scaling factor from baseline shore (55). */
function computeScaleFactor(newShA: number): number {
  return 1.0 + 0.035 * (newShA - BASELINE_SHORE);
}

export interface ShoreScalingResult {
  /** Apply shore scaling to the currently selected sample. */
  applyShoreScaling: (newShA: number) => void;
  /** Capture baseline values for a sample (call after compute). */
  captureBaseline: (sampleId: string) => void;
  /** Whether scaling is available for the current selection. */
  canScale: boolean;
}

export function useShoreScaling(): ShoreScalingResult {
  const bushingType = useBushingStore((s) => s.bushingType);
  const currentSelectedId = useSampleStore((s) => s.currentSelectedId);
  const setShoreBaseline = useSampleStore((s) => s.setShoreBaseline);
  const updateGenRow = useSampleStore((s) => s.updateGenRow);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canScale =
    currentSelectedId !== null &&
    currentSelectedId.startsWith('N') &&
    bushingType !== 'BYD EV Voided';

  const captureBaseline = useCallback(
    (sampleId: string) => {
      // Read directly from store to avoid stale closure issues
      const row = useSampleStore.getState().genRowsById[sampleId];
      if (!row) return;
      const cols = getScalableColumns(bushingType);
      const baselines: Record<string, number> = {};
      for (const col of cols) {
        const val = row[col];
        if (typeof val === 'number' && isFinite(val)) {
          baselines[col] = val;
        }
      }
      setShoreBaseline(sampleId, baselines);
    },
    [bushingType, setShoreBaseline],
  );

  const applyShoreScaling = useCallback(
    (newShA: number) => {
      if (!currentSelectedId) return;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        // Read directly from store to avoid stale closure issues
        const baselines = useSampleStore.getState().shoreBaselines[currentSelectedId];
        if (!baselines) return;

        const clamped = Math.max(45, Math.min(65, newShA));
        const factor = computeScaleFactor(clamped);

        const patch: Partial<GenRow> = { ShA: clamped };
        for (const [col, baseVal] of Object.entries(baselines)) {
          patch[col] = baseVal * factor;
        }

        updateGenRow(currentSelectedId, patch);
      }, DEBOUNCE_MS);
    },
    [currentSelectedId, updateGenRow],
  );

  return { applyShoreScaling, captureBaseline, canScale };
}
